import dayjs from 'dayjs'
import { Navigate, Outlet, Route, Routes } from 'react-router-dom'

import { ContractsDashboard, RoutesDashboard } from '@components'
import { ROUTE_KEYS } from '@constants'
import { FiltersProvider, useGeneral, useUser } from '@contexts'
import { RoleEnum, StatusTicketEnum } from '@types'

import { CertifiedAdmin } from '../../components/BatchesCertifiedAdmin'
import { ClosedAdmin } from '../../components/BatchesClosedAdmin'
import { OpenAdmin } from '../../components/BatchesOpenAdmin'
import PrivateRoutes from '../../components/PrivateRoutes'
import { Settings } from '../../components/SettingsDashboard'
import { Simulation } from '../../components/SettingsDashboard/simulation'
import SuppliersView from '../../components/SuppliersView'
import TicketView from '../../components/TicketView'
import { FiltersProviderV2 } from '../../contexts/FiltersContextV2'
import { AdminRoRouter } from '../AdminRoRouter'
import { AdminRouter } from '../AdminRouter'
import AdminEVouchers from '../AdminRouter/AdminEVouchers'
import { BlocktrainsAdminView } from '../AdminRouter/BlocktrainsAdminView'
import BlockTrainDetailAdmin from '../AdminRouter/BlocktrainsAdminView/BlockTrainDetailAdmin'
import { BlocktrainHistoricViewAdmin } from '../AdminRouter/BlocktrainsAdminView/BlocktrainHistoricViewAdmin'
import { Ongoing } from '../AdminRouter/Requests/Ongoing'
import { LoginLayout } from '../Login'
import { ChangePassword } from '../Login/ChangePassword'
import { Login, LoginError } from '../Login/Login'
import { LoginSSO } from '../Login/LoginSSO'
import { LoginTraditional } from '../Login/LoginTraditional'
import { RecoverPassword } from '../Login/RecoverPassword'
import { SetToken } from '../Saml/Login'
import { Logout } from '../Saml/Logout'
import { SuppliersRouter } from '../SuppliersRouter'
import BlocktrainsSupplierView from '../SuppliersRouter/BlocktrainsSupplierView'
import BlockTrainDetail from '../SuppliersRouter/BlocktrainsSupplierView/BlockTrainDetailSupplier'
import BlocktrainHistoricViewSupplier from '../SuppliersRouter/BlocktrainsSupplierView/BlocktrainHistoricViewSupplier'
import { SuppliersDashboard } from '../SuppliersRouter/SuppliersDashboard'
import TicketRouter from '../TicketRouter'

const { LOGIN, TOKEN, LOGOUT, ROOT, ADMINRO, SUPPLIER, ADMIN } = ROUTE_KEYS
const { PENDING, CREATED, CONFIRMED, TRANSIT, DELIVERED } = StatusTicketEnum

const AppRoutes = () => {
  const { maxDaysRange } = useGeneral()
  const { state: user } = useUser()

  return (
    <Routes>
      <Route path={ROOT}>
        <Route index element={<Navigate to={LOGIN.HOME} />} />
        <Route path="login" element={<LoginLayout />}>
          <Route index element={<Login />} />
          <Route path={LOGIN.TRADITIONAL} element={<LoginTraditional />} />
          <Route path={LOGIN.SSO} element={<LoginSSO />} />
          <Route path={LOGIN.RECOVER} element={<RecoverPassword />} />
          <Route path={LOGIN.CHANGE_PASSWORD} element={<ChangePassword />} />
          <Route path={LOGIN.LOGIN_ERROR} element={<LoginError />} />
        </Route>
        <Route path={TOKEN} element={<SetToken />} />
        <Route path={LOGOUT} element={<Logout />} />
        <Route path="dashboard" element={user ? <Outlet /> : <Navigate to={`/${LOGIN.HOME}`} />}>
          <Route
            path="suppliers"
            element={
              <PrivateRoutes allowedRoles={[RoleEnum.SUPPLIER]}>
                <SuppliersRouter />
              </PrivateRoutes>
            }
          >
            <Route
              index
              element={<Navigate to={user ? SUPPLIER.BLOCKTRAINS : `/${LOGIN.HOME}`} />}
            />

            <Route path="blocktrains">
              <Route
                path=""
                index
                element={
                  <FiltersProviderV2
                    key="filtersSuppliersBlocktrains"
                    customized={{ limit: 10, orderBy: { id: 'DESC' } }}
                  >
                    <BlocktrainsSupplierView />
                  </FiltersProviderV2>
                }
              />
              <Route path=":id" element={<BlockTrainDetail />} />
            </Route>
            <Route
              path="historic"
              element={
                <FiltersProviderV2
                  key="filtersStatusFinishedSupplier"
                  customized={{
                    orderBy: { id: 'DESC' },
                  }}
                >
                  <BlocktrainHistoricViewSupplier />
                </FiltersProviderV2>
              }
            />
            <Route
              path="previous"
              element={
                <FiltersProvider
                  key="filtersSupplierPrevious"
                  customized={{
                    dateFrom: dayjs()
                      .subtract(maxDaysRange + 1, 'd')
                      .startOf('d')
                      .toDate(),
                    dateTo: dayjs().endOf('d').toDate(),
                    previous: true,
                  }}
                >
                  <SuppliersDashboard />
                </FiltersProvider>
              }
            />
          </Route>

          <Route
            path="admin"
            element={
              <PrivateRoutes allowedRoles={[RoleEnum.ADMIN]}>
                <AdminRouter />
              </PrivateRoutes>
            }
          >
            <Route index element={<Navigate to={user ? ADMIN.BLOCKTRAINS : `/${LOGIN.HOME}`} />} />
            <Route path="blocktrains">
              <Route
                path=""
                index
                element={
                  <FiltersProviderV2
                    key="filtersAdminBlocktrains"
                    customized={{ limit: 10, orderBy: { id: 'DESC' } }}
                  >
                    <BlocktrainsAdminView />
                  </FiltersProviderV2>
                }
              />
              <Route path=":id" element={<BlockTrainDetailAdmin />} />
            </Route>
            <Route
              path="historic"
              element={
                <FiltersProviderV2
                  key="filtersStatusFinishedAdmin"
                  customized={{
                    orderBy: { id: 'DESC' },
                  }}
                >
                  <BlocktrainHistoricViewAdmin />
                </FiltersProviderV2>
              }
            />
            <Route
              path="ongoing"
              element={
                <FiltersProvider
                  key="filtersAdminOngoing"
                  customized={{
                    mondayOfWeek: dayjs().isoWeekday(1).toDate(),
                    statusName: [PENDING, CREATED, CONFIRMED, TRANSIT, DELIVERED],
                  }}
                >
                  <Ongoing />
                </FiltersProvider>
              }
            />
            <Route index element={<Navigate to={user ? ADMIN.BLOCKTRAINS : `/${LOGIN.HOME}`} />} />
            <Route path=":id" element={<BlockTrainDetail />} />
            <Route
              path="evouchers"
              element={
                <FiltersProvider
                  key="filtersAdminEvouchers"
                  customized={{
                    dateFrom: dayjs()
                      .subtract(maxDaysRange + 1, 'd')
                      .startOf('d')
                      .toDate(),
                    dateTo: dayjs().add(1, 'd').endOf('d').toDate(),
                    evouchers: true,
                  }}
                >
                  <AdminEVouchers />
                </FiltersProvider>
              }
            />
            <Route
              path="routes"
              element={
                <FiltersProvider key="filtersAdminRoutes">
                  <RoutesDashboard />
                </FiltersProvider>
              }
            />
            <Route path="ticket/:id" element={<TicketView />} />
            <Route path="contracts" element={<ContractsDashboard />} />
            <Route path="settings" element={<Settings />} />
            <Route path="simulation" element={<Simulation />} />
            <Route path="open" element={<OpenAdmin />} />
            <Route path="closed" element={<ClosedAdmin />} />
            <Route path="certified" element={<CertifiedAdmin />} />
            <Route
              path="suppliers"
              element={
                <FiltersProviderV2 key="filtersAdminSuppliers" customized={{ limit: 10 }}>
                  <SuppliersView />
                </FiltersProviderV2>
              }
            />
          </Route>
          <Route
            path={ADMINRO.HOME}
            element={
              <PrivateRoutes allowedRoles={[RoleEnum.ADMINRO]}>
                <AdminRoRouter />
              </PrivateRoutes>
            }
          >
            <Route
              index
              element={<Navigate to={user ? ADMINRO.BLOCKTRAINS : `/${LOGIN.HOME}`} />}
            />
            <Route path="blocktrains">
              <Route
                path=""
                index
                element={
                  <FiltersProviderV2
                    key="filtersAdminBlocktrains"
                    customized={{ limit: 10, orderBy: { id: 'DESC' } }}
                  >
                    <BlocktrainsAdminView />
                  </FiltersProviderV2>
                }
              />
              <Route path=":id" element={<BlockTrainDetailAdmin />} />
            </Route>
            <Route
              path="historic"
              element={
                <FiltersProviderV2
                  key="filtersStatusFinishedAdmin"
                  customized={{
                    orderBy: { id: 'DESC' },
                  }}
                >
                  <BlocktrainHistoricViewAdmin />
                </FiltersProviderV2>
              }
            />
            <Route
              path="ongoing"
              element={
                <FiltersProvider
                  key="filtersAdminOngoing"
                  customized={{
                    mondayOfWeek: dayjs().isoWeekday(1).toDate(),
                    statusName: [PENDING, CREATED, CONFIRMED, TRANSIT, DELIVERED],
                  }}
                >
                  <Ongoing />
                </FiltersProvider>
              }
            />
            <Route index element={<Navigate to={user ? ADMIN.BLOCKTRAINS : `/${LOGIN.HOME}`} />} />
            <Route path=":id" element={<BlockTrainDetail />} />
            <Route
              path="evouchers"
              element={
                <FiltersProvider
                  key="filtersAdminEvouchers"
                  customized={{
                    dateFrom: dayjs()
                      .subtract(maxDaysRange + 1, 'd')
                      .startOf('d')
                      .toDate(),
                    dateTo: dayjs().add(1, 'd').endOf('d').toDate(),
                    evouchers: true,
                  }}
                >
                  <AdminEVouchers />
                </FiltersProvider>
              }
            />
            <Route
              path="routes"
              element={
                <FiltersProvider key="filtersAdminRoutes">
                  <RoutesDashboard />
                </FiltersProvider>
              }
            />
            <Route path="ticket/:id" element={<TicketView />} />
            <Route path="contracts" element={<ContractsDashboard />} />
            <Route path="settings" element={<Settings />} />
            <Route path="simulation" element={<Simulation />} />
            <Route path="open" element={<OpenAdmin />} />
            <Route path="closed" element={<ClosedAdmin />} />
            <Route path="certified" element={<CertifiedAdmin />} />
            <Route
              path="suppliers"
              element={
                <FiltersProviderV2 key="filtersAdminSuppliers" customized={{ limit: 10 }}>
                  <SuppliersView />
                </FiltersProviderV2>
              }
            />
          </Route>
          <Route
            path="ticket/:id"
            element={
              <PrivateRoutes allowedRoles={[RoleEnum.ADMIN, RoleEnum.ADMINRO]}>
                <TicketRouter />
              </PrivateRoutes>
            }
          />
        </Route>
      </Route>
    </Routes>
  )
}

export default AppRoutes
